// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faTags,
  faArrowLeft,
  faMoneyBillWave,
  faCalculator,
  faTrash,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
// eslint-disable-next-line no-unused-vars
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import SecureLS from "secure-ls";
import TopActionMenus from "./TopActionMenus";
import { useNavigationEvent } from "navigation-react";

const ls = new SecureLS({ encodingType: "aes" });

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function StockInventory() {
  const { stateNavigator } = useNavigationEvent();
  const [searchTerm, setSearchTerm] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [filterType, setFilterType] = useState("all"); // 'all', 'received', 'issue'
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch stock movements
  const { data: stockMovements, mutate: mutateStockMovements } = useSWR(
    `${process.env.REACT_APP_STOCK_MOVEMENT_API}?include=field_product,field_product_supplier&fields[node--stock_movement]=title,field_quantity,field_stock_movement_cost_price,field_markup,field_selling_price,field_invoice_number,field_date_received,field_movement_type,field_notes,field_product,field_product_supplier&page[limit]=200&sort=-created`,
    fetcher,
  );
  console.log("Stock Movements:", stockMovements);

  // Fetch products
  const { data: productList, mutate: mutateProducts } = useSWR(
    `${process.env.REACT_APP_ADD_PRODUCT_API}?fields[node--product]=title,field_sku,field_stock_quantity,field_price&page[limit]=1000`,
    fetcher,
  );
  console.log("Product List:", productList);

  // Create lookup objects
  const productLookup =
    productList?.data?.reduce((acc, product) => {
      acc[product.id] = {
        title: product.attributes.title,
        sku: product.attributes.field_sku,
        current_stock: parseInt(product.attributes.field_stock_quantity) || 0,
        selling_price: parseFloat(product.attributes.field_price) || 0,
      };
      return acc;
    }, {}) || {};

  // Filter movements
  const filteredMovements = () => {
    if (!stockMovements?.data) return [];

    let filtered = stockMovements.data.filter((movement) => {
      // Filter by type
      if (
        filterType !== "all" &&
        movement.attributes.field_movement_type !== filterType
      ) {
        return false;
      }

      // Filter by search
      if (searchTerm) {
        const product =
          productLookup[movement.relationships?.field_product?.data?.id];
        const productName = product?.title || "";
        const invoice = movement.attributes.field_invoice_number || "";

        return (
          productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          invoice.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      return true;
    });

    return filtered;
  };

  // Get filtered movements
  const movements = filteredMovements();

  // Calculate inventory statistics BASED ON FILTERED MOVEMENTS
  const calculateStats = (movementsToCalculate = movements) => {
    if (!movementsToCalculate || movementsToCalculate.length === 0) {
      return {
        totalCostValue: 0,
        totalSellingValue: 0,
        totalProfit: 0,
        profitMarkup: 0,
        inventoryValue: 0,
        averageSellingPrice: 0,
        totalItems: 0,
      };
    }

    let totalCostValue = 0;
    let totalSellingValue = 0;
    let totalProfit = 0;
    let totalItems = 0;
    let totalSellingPriceSum = 0;
    let sellingPriceCount = 0;

    // Calculate from filtered movements
    movementsToCalculate.forEach((movement) => {
      const quantity = parseInt(movement.attributes.field_quantity) || 0;
      const costPrice =
        parseFloat(movement.attributes.field_stock_movement_cost_price) || 0;
      const sellingPrice =
        parseFloat(movement.attributes.field_selling_price) || 0;
      const markup = parseFloat(movement.attributes.field_markup) || 0;

      const isReceived = movement.attributes.field_movement_type === "received";

      // Only calculate for received stock movements
      if (isReceived && quantity > 0 && costPrice > 0) {
        totalItems += quantity;

        // Calculate cost value
        const itemCostValue = quantity * costPrice;
        totalCostValue += itemCostValue;

        // Calculate selling value
        let itemSellingValue = 0;
        let effectiveSellingPrice = 0;

        // Priority 1: Use sellingPrice from Drupal
        if (sellingPrice > 0) {
          effectiveSellingPrice = sellingPrice;
          itemSellingValue = quantity * sellingPrice;
          totalSellingPriceSum += sellingPrice;
          sellingPriceCount++;
        }
        // Priority 2: Calculate from markup
        else if (markup > 0) {
          effectiveSellingPrice = costPrice * (1 + markup / 100);
          itemSellingValue = quantity * effectiveSellingPrice;
          totalSellingPriceSum += effectiveSellingPrice;
          sellingPriceCount++;
        }
        // Priority 3: Use costPrice as sellingPrice (no profit)
        else {
          effectiveSellingPrice = costPrice;
          itemSellingValue = quantity * costPrice;
          totalSellingPriceSum += effectiveSellingPrice;
          sellingPriceCount++;
        }

        totalSellingValue += itemSellingValue;
        totalProfit += itemSellingValue - itemCostValue;
      }
    });

    // Calculate current inventory value for filtered products
    let inventoryValue = 0;
    if (productList?.data) {
      // Get unique product IDs from filtered movements
      const filteredProductIds = new Set();
      movementsToCalculate.forEach((movement) => {
        const productId = movement.relationships?.field_product?.data?.id;
        if (productId) {
          filteredProductIds.add(productId);
        }
      });

      // If there's a search filter, only calculate inventory for filtered products
      // Otherwise, calculate for all products
      if (searchTerm || filterType !== "all") {
        productList.data.forEach((product) => {
          if (filteredProductIds.has(product.id)) {
            const stock =
              parseInt(product.attributes.field_stock_quantity) || 0;
            const price = parseFloat(product.attributes.field_price) || 0;
            inventoryValue += stock * price;
          }
        });
      } else {
        // Calculate for all products when no filter is applied
        productList.data.forEach((product) => {
          const stock = parseInt(product.attributes.field_stock_quantity) || 0;
          const price = parseFloat(product.attributes.field_price) || 0;
          inventoryValue += stock * price;
        });
      }
    }

    const profitMarkup =
      totalCostValue > 0 ? (totalProfit / totalCostValue) * 100 : 0;
    const averageSellingPrice =
      sellingPriceCount > 0 ? totalSellingPriceSum / sellingPriceCount : 0;

    return {
      totalCostValue: totalCostValue.toFixed(2),
      totalSellingValue: totalSellingValue.toFixed(2),
      totalProfit: totalProfit.toFixed(2),
      profitMarkup: profitMarkup.toFixed(1),
      inventoryValue: inventoryValue.toFixed(2),
      averageSellingPrice: averageSellingPrice.toFixed(2),
      totalItems,
    };
  };

  // Calculate stats based on filtered movements
  const stats = calculateStats();

  // Function to delete stock movement - SIMPLIFIED VERSION
  const deleteStockMovement = async (movementId, movementData) => {
    if (!movementId) {
      console.error("Invalid stock movement ID");
      return;
    }

    try {
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        alert("Authentication failed");
        return;
      }

      // Show confirmation dialog
      if (
        window.confirm(
          "Are you sure you want to delete this stock invoice? This will automatically reverse the stock quantity.",
        )
      ) {
        // Just send DELETE request - Drupal module will handle stock reversal
        const response = await fetch(
          `${process.env.REACT_APP_STOCK_MOVEMENT_API}/${movementId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/vnd.api+json",
              Accept: "application/vnd.api+json",
              "X-CSRF-Token": csrfToken,
            },
            credentials: "include",
          },
        );

        if (response.ok) {
          // Show success message
          alert(
            "Stock movement deleted successfully. Stock quantity has been reversed.",
          );

          // Refresh data
          mutateStockMovements();
          mutateProducts();
        } else if (response.status === 403) {
          // Handle deletion prevented by Drupal
          const errorData = await response.json();
          alert(
            `Cannot delete: ${
              errorData.message || "Product has been sold in sales orders."
            }`,
          );
        } else {
          alert(
            `This invoice has a sales attached. Cannot be deleted. Error: ${response.status}`,
          );
        }
      }
    } catch (error) {
      console.error("Error deleting stock movement:", error);
      alert("An error occurred while deleting the stock movement.");
    }
  };

  // Pagination
  const totalPages = Math.ceil(movements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMovements = movements.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="inventory-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Inventory"
            buttonLabel="Receive stock"
            icon={faArrowLeft}
            onClick={() => stateNavigator.navigate("receivestock")}
          />

          {/* STATISTICS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faMoneyBillWave} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>GH₵{stats.totalSellingValue}</h3>
                <p style={styles.statLabel}>Total Selling Value</p>
                {searchTerm || filterType !== "all" ? (
                  <small style={{ color: "#666", fontSize: "12px" }}>
                    (Filtered)
                  </small>
                ) : null}
              </div>
            </div>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faCalculator} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>GH₵{stats.totalCostValue}</h3>
                <p style={styles.statLabel}>Total Cost Value</p>
                {searchTerm || filterType !== "all" ? (
                  <small style={{ color: "#666", fontSize: "12px" }}>
                    (Filtered)
                  </small>
                ) : null}
              </div>
            </div>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faTags} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>GH₵{stats.inventoryValue}</h3>
                <p style={styles.statLabel}>Current Inventory Value</p>
                {searchTerm || filterType !== "all" ? (
                  <small style={{ color: "#666", fontSize: "12px" }}>
                    (Filtered)
                  </small>
                ) : null}
              </div>
            </div>
          </div>

          {/* FILTERS AND SEARCH */}
          <div style={styles.filtersSection}>
            <div style={styles.searchContainer}>
              <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by product name or invoice number..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page when searching
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "#666",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* STOCK MOVEMENTS TABLE */}
          <div style={styles.card}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Invoice Date</th>
                    <th style={styles.th}>Invoice #</th>
                    <th style={styles.th}>Product</th>
                    <th style={styles.th}>Qty</th>
                    <th style={styles.th}>Cost Price</th>
                    <th style={styles.th}>Markup %</th>
                    <th style={styles.th}>Selling Price</th>
                    <th style={styles.th}>Total Cost</th>
                    <th style={styles.th}>Total Selling</th>
                    <th style={styles.th}>Profit</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!stockMovements ? (
                    <tr>
                      <td
                        colSpan="11"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading
                        inventory...
                      </td>
                    </tr>
                  ) : paginatedMovements.length === 0 ? (
                    <tr>
                      <td
                        colSpan="11"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        {searchTerm || filterType !== "all"
                          ? "No movements match your search criteria"
                          : "No stock movements found"}
                      </td>
                    </tr>
                  ) : (
                    paginatedMovements.map((movement) => {
                      const quantity =
                        parseInt(movement.attributes.field_quantity) || 0;
                      const costPrice =
                        parseFloat(
                          movement.attributes.field_stock_movement_cost_price,
                        ) || 0;
                      const markup =
                        parseFloat(movement.attributes.field_markup) || 0;
                      const sellingPrice =
                        parseFloat(movement.attributes.field_selling_price) ||
                        0;

                      const totalCost = quantity * costPrice;

                      // Calculate selling price
                      let displaySellingPrice = 0;
                      if (sellingPrice > 0) {
                        displaySellingPrice = sellingPrice;
                      } else if (markup > 0) {
                        displaySellingPrice = costPrice * (1 + markup / 100);
                      } else {
                        displaySellingPrice = costPrice;
                      }

                      const totalSelling = quantity * displaySellingPrice;
                      const profit = totalSelling - totalCost;

                      return (
                        <tr key={movement.id} style={styles.tableRow}>
                          <td style={styles.td}>
                            {formatDisplayDate(
                              movement.attributes.field_date_received,
                            )}
                          </td>
                          <td style={styles.td}>
                            {movement.attributes.field_invoice_number || "N/A"}
                          </td>
                          <td style={styles.td}>
                            {productLookup[
                              movement.relationships?.field_product?.data?.id
                            ]?.title || "N/A"}
                          </td>
                          <td style={styles.td}>{quantity}</td>
                          <td style={styles.td}>GH₵{costPrice.toFixed(2)}</td>
                          <td style={styles.td}>
                            {markup > 0 ? markup.toFixed(2) : "N/A"}
                          </td>
                          <td style={styles.td}>
                            GH₵{displaySellingPrice.toFixed(2)}
                          </td>
                          <td style={styles.td}>GH₵{totalCost.toFixed(2)}</td>
                          <td style={styles.td}>
                            {totalSelling
                              ? `GH₵${totalSelling.toFixed(2)}`
                              : "N/A"}
                          </td>
                          <td style={styles.td}>
                            {profit ? `GH₵${profit.toFixed(2)}` : "N/A"}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actionsContainer}>
                              <button
                                style={styles.viewButton}
                                title="View Details"
                                onClick={() => console.log("View", movement.id)}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                style={styles.deleteButton}
                                title="Delete Stock Movement"
                                onClick={() =>
                                  deleteStockMovement(movement.id, movement)
                                }
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                <div style={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        style={{
                          ...styles.pageButton,
                          ...(currentPage === pageNum
                            ? styles.activePageButton
                            : {}),
                        }}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>

                <div style={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DashboardContentFooter />
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    color: "#333",
    marginBottom: "5px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  subtitle: {
    color: "#666",
    fontSize: "16px",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    marginBottom: "30px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  statIcon: {
    fontSize: "22px",
    color: "#007bff",
  },
  statValue: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "700",
    color: "#333",
  },
  statLabel: {
    margin: "5px 0 0 0",
    color: "#666",
    fontSize: "14px",
  },
  filtersSection: {
    display: "flex",
    gap: "20px",
    marginBottom: "20px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchContainer: {
    flex: 1,
    position: "relative",
    minWidth: "300px",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#666",
  },
  searchInput: {
    width: "100%",
    padding: "16px 10px 16px 40px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  filterLabel: {
    color: "#666",
    fontSize: "14px",
  },
  filterSelect: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    background: "white",
    fontSize: "14px",
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "10px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1500px",
    borderRadius: "8px",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f5f9ff",
    },
  },
  th: {
    background: "#000000",
    padding: "12px",
    textAlign: "left",
    borderBottom: "2px solid #dee2e6",
    color: "#fff",
    fontWeight: "600",
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #eee",
    verticalAlign: "top",
  },
  marginBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
  },
  // Actions Styles
  actionsContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  viewButton: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#bbdefb",
    },
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#ffcdd2",
    },
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e0e0e0",
  },
  paginationButton: {
    background: "#f8f9fa",
    border: "1px solid #dee2e6",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    "&:hover:not(:disabled)": {
      background: "#e9ecef",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  pageNumbers: {
    display: "flex",
    gap: "8px",
  },
  pageButton: {
    background: "white",
    border: "1px solid #dee2e6",
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    minWidth: "40px",
    "&:hover": {
      background: "#f8f9fa",
    },
  },
  activePageButton: {
    background: "#007bff",
    color: "white",
    borderColor: "#007bff",
    "&:hover": {
      background: "#0056b3",
    },
  },
  pageInfo: {
    color: "#666",
    fontSize: "14px",
  },
};
